import React, { useMemo, useState } from "react";
import "./intelligence.css";

/* ---------- formatting ---------- */

export const fmtNum = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? "–" : Number(n).toLocaleString("en-IN"));

export const fmtMoney = (amount, currency = "INR", compact = true) => {
  const n = Number(amount) || 0;
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (currency === "USD") {
    if (compact && a >= 1000) return `${sign}$${(a / 1000).toFixed(a >= 100000 ? 0 : 1)}K`;
    return `${sign}$${a.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  if (compact && a >= 10000000) return `${sign}₹${(a / 10000000).toFixed(2)}Cr`;
  if (compact && a >= 100000) return `${sign}₹${(a / 100000).toFixed(a >= 1000000 ? 1 : 2)}L`;
  return `${sign}₹${a.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

/** money object {INR, USD} -> "₹6.4L" or "₹6.4L + $1.9K" */
export const fmtMoneyObj = (m, compact = true) => {
  if (!m) return "–";
  const parts = [];
  if (m.INR) parts.push(fmtMoney(m.INR, "INR", compact));
  if (m.USD) parts.push(fmtMoney(m.USD, "USD", compact));
  return parts.length ? parts.join(" + ") : (m.INR === 0 && m.USD === 0 ? "₹0" : "–");
};

export const fmtPct = (v, digits = 1) => (v === null || v === undefined ? "–" : `${Number(v).toFixed(digits).replace(/\.0$/, "")}%`);
export const fmtDays = (d) => (d === null || d === undefined ? "–" : `${Number(d).toFixed(1).replace(/\.0$/, "")} d`);
export const fmtHours = (h) => (h === null || h === undefined ? "–" : `${Math.round(h)} h`);
export const fmtDate = (s) => {
  if (!s) return "–";
  const d = new Date(s.length === 10 ? s + "T00:00:00" : s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
export const rateCardLabel = (c) => ({ highDemand: "High demand", niche: "Niche", legacy: "Legacy", leadership: "Leadership", others: "Others" }[c] || c || "–");
export const rateCategoryLabel = (c) => ({ ratesInIndia: "India", ratesInUS: "US", ratesInternal: "Internal", ratesExternal: "External" }[c] || c || "–");

export const STATUS_COLORS = {
  completed: "#2E8B57", scheduled: "#3E67B1", candidateNoShow: "#D9822B", panelNoShow: "#B4621A",
  cancelled: "#C0392B", rescheduled: "#7A6FBE", glitches: "#6B7280"
};
export const STATUS_LABELS = {
  completed: "Completed", scheduled: "Scheduled", candidateNoShow: "Candidate no-show", panelNoShow: "Panel no-show",
  cancelled: "Cancelled", rescheduled: "Rescheduled", glitches: "Technical glitch"
};

/* ---------- period presets ---------- */

const toDay = (d) => d.toISOString().slice(0, 10);
export const PRESETS = [
  { key: "30d", label: "Last 30 days", range: () => { const e = new Date(); const s = new Date(); s.setDate(e.getDate() - 29); return { startDate: toDay(s), endDate: toDay(e) }; } },
  { key: "month", label: "This month", range: () => { const e = new Date(); const s = new Date(e.getFullYear(), e.getMonth(), 1); return { startDate: toDay(new Date(Date.UTC(s.getFullYear(), s.getMonth(), 1))), endDate: toDay(e) }; } },
  { key: "90d", label: "Last 90 days", range: () => { const e = new Date(); const s = new Date(); s.setDate(e.getDate() - 89); return { startDate: toDay(s), endDate: toDay(e) }; } },
  { key: "ytd", label: "This year", range: () => { const e = new Date(); return { startDate: `${e.getFullYear()}-01-01`, endDate: toDay(e) }; } },
  { key: "custom", label: "Custom", range: null }
];

export function PeriodPicker({ preset, setPreset, range, setRange }) {
  return (
    <>
      {PRESETS.map((p) => (
        <button key={p.key} type="button" className={`intel-chip ${preset === p.key ? "active" : ""}`}
          onClick={() => { setPreset(p.key); if (p.range) setRange(p.range()); }}>
          {p.label}
        </button>
      ))}
      {preset === "custom" && (
        <>
          <input type="date" className="intel-input" style={{ minWidth: 150 }} value={range.startDate} max={range.endDate}
            onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
          <span className="muted">to</span>
          <input type="date" className="intel-input" style={{ minWidth: 150 }} value={range.endDate} min={range.startDate}
            onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
        </>
      )}
    </>
  );
}

/* ---------- building blocks ---------- */

export function Kpi({ label, value, sub, tone, valueTone, help, border }) {
  return (
    <div className={`kpi ${border || ""}`}>
      <div className="label">
        <span>{label}</span>
        {help && <span className="help" title={help}>?</span>}
      </div>
      <div className={`value ${valueTone || ""}`}>{value}</div>
      {sub && <div className={`sub ${tone || ""}`}>{sub}</div>}
    </div>
  );
}

/** "▲ 12% vs prior period" with the right colour. higherIsBetter flips the colour logic. */
export function Delta({ value, higherIsBetter = true, suffix = "vs prior period", pts = false }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const v = Number(value);
  if (v === 0) return <span className="muted">No change {suffix}</span>;
  const up = v > 0;
  const good = higherIsBetter ? up : !up;
  return (
    <span className={good ? "good strong" : "bad strong"}>
      {up ? "▲" : "▼"} {Math.abs(v).toFixed(1).replace(/\.0$/, "")}{pts ? " pts" : "%"} {suffix}
    </span>
  );
}

export function Card({ title, hint, superOnly, children, foot, className = "", style }) {
  return (
    <div className={`intel-card ${superOnly ? "super" : ""} ${className}`} style={style}>
      <div className="intel-card-head">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span>{title}</span>
          {superOnly && <span className="pill solid"><LockIcon /> Super admin only</span>}
        </div>
        {hint && <span className="hint">{hint}</span>}
      </div>
      {children}
      {foot && <div className="intel-card-foot">{foot}</div>}
    </div>
  );
}

export function Pill({ tone, children }) { return <span className={`pill ${tone || "grey"}`}>{children}</span>; }

export const healthPill = (h) => {
  if (h === "AT RISK") return <Pill tone="red">At risk</Pill>;
  if (h === "WATCH") return <Pill tone="amber">Watch</Pill>;
  if (h === "HEALTHY") return <Pill tone="green">Healthy</Pill>;
  return <Pill>{h || "–"}</Pill>;
};
export const bandPill = (b) => {
  if (b === "EXCEEDS") return <Pill tone="green">Exceeds</Pill>;
  if (b === "MEETS") return <Pill tone="blue">Meets</Pill>;
  if (b === "NEEDS SUPPORT") return <Pill tone="amber">Needs support</Pill>;
  return <Pill>{b || "–"}</Pill>;
};
export const severityPill = (s) => {
  if (s === "high") return <Pill tone="red">High</Pill>;
  if (s === "medium") return <Pill tone="amber">Medium</Pill>;
  return <Pill tone="blue">Info</Pill>;
};

export function StatusBars({ mix }) {
  if (!mix) return null;
  const total = mix.total || 0;
  const rows = ["completed", "scheduled", "rescheduled", "candidateNoShow", "panelNoShow", "cancelled", "glitches"]
    .map((k) => ({ k, v: mix[k] || 0 })).filter((r) => r.v > 0 || ["completed", "scheduled", "cancelled"].includes(r.k));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {rows.map((r) => (
        <div key={r.k}>
          <div className="bar-row"><span>{STATUS_LABELS[r.k]}</span><span className="strong">{fmtNum(r.v)} · {total ? Math.round((r.v / total) * 100) : 0}%</span></div>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${total ? (r.v / total) * 100 : 0}%`, background: STATUS_COLORS[r.k] }} /></div>
        </div>
      ))}
    </div>
  );
}

export function MiniBar({ pct, color = "#2E8B57" }) {
  const w = Math.max(0, Math.min(100, Number(pct) || 0));
  return <span className="mini-track"><span className="mini-fill" style={{ width: `${w}%`, background: color, display: "block" }} /></span>;
}

export function Funnel({ funnel }) {
  if (!funnel) return null;
  const steps = [
    ["Candidates submitted", funnel.submitted, "#3E67B1", "#FFFFFF"],
    ["Claimed by a panel", funnel.claimed, "#5A7FC2", "#FFFFFF"],
    ["Interview scheduled", funnel.scheduled, "#7A98D0", "#FFFFFF"],
    ["Interview completed", funnel.completed, "#9BB2DD", "#3A3A3A"],
    ["Feedback provided", funnel.feedback, "#BFCDE9", "#3A3A3A"],
    ["Selected", funnel.selected, "#2E8B57", "#FFFFFF"]
  ];
  const max = funnel.submitted || 1;
  let biggestDrop = null;
  for (let i = 1; i < steps.length - 1; i++) {
    const drop = steps[i - 1][1] - steps[i][1];
    if (!biggestDrop || drop > biggestDrop.drop) biggestDrop = { drop, from: steps[i - 1][0], to: steps[i][0] };
  }
  return (
    <div>
      {steps.map(([label, v, bg, fg], i) => (
        <div className="funnel-step" key={label}>
          <div className="funnel-bar" style={{ width: `${Math.max(18, (v / max) * 100)}%`, background: bg, color: fg }}>{label} · {fmtNum(v)}</div>
          {i > 0 && <span className="funnel-note">{max ? Math.round((v / max) * 100) : 0}%</span>}
        </div>
      ))}
      {biggestDrop && biggestDrop.drop > 0 && (
        <div className="funnel-note" style={{ marginTop: 8 }}>Biggest drop: {biggestDrop.from} → {biggestDrop.to} ({fmtNum(biggestDrop.drop)} candidates).</div>
      )}
    </div>
  );
}

export function Legend({ keys }) {
  return (
    <div className="legend">
      {keys.map((k) => <span key={k}><i style={{ background: STATUS_COLORS[k] }} />{STATUS_LABELS[k]}</span>)}
    </div>
  );
}

/** Simple sortable table helper */
export function useSort(rows, initialKey, initialDir = "desc") {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const sorted = useMemo(() => {
    const r = [...(rows || [])];
    const { key, dir } = sort;
    r.sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [rows, sort]);
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const Th = ({ k, children, num }) => (
    <th className={`sortable ${num ? "num" : ""}`} onClick={() => toggle(k)}>
      {children}{sort.key === k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
  return { sorted, Th };
}

export function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export function Forbidden() {
  return (
    <div className="intel-page">
      <div className="intel-card"><div className="intel-empty">This page is available to the Super Admin only.</div></div>
    </div>
  );
}

export function Loading({ text = "Loading dashboard…" }) { return <div className="intel-loading">{text}</div>; }

export const isSuperAdmin = () => {
  try { return (JSON.parse(localStorage.getItem("user") || "{}").profileType || "").toUpperCase() === "SUPER"; } catch (e) { return false; }
};
